<template>
  <div>
    <el-dialog
      :title="$t('Household.DeviceRFIDDetailDialog.Title', { tabletNote })"
      :visible.sync="dialogVisible"
      width="1000px"
      class="device-rfid-dialog"
      :before-close="beforeClose"
      @close="onCloseDialog"
    >
      <div class="device-dialog-content">
        <!-- Tab 切換區域 -->
        <el-tabs v-model="activeTab" :before-leave="onBeforeLeave">
          <!-- 手機裝置 Tab -->
          <el-tab-pane name="device">
            <el-tooltip
              slot="label"
              :content="deviceTabTooltip"
              placement="top"
              :disabled="!isVirtualEnabled"
            >
              <span>
                <el-badge :value="deviceList.length" class="read-dialog__badge"> {{ $t('Household.DeviceRFIDDetailDialog.Tabs.Device') }} </el-badge>
              </span>
            </el-tooltip>
            <!-- 操作按鈕區域 -->
            <div class="operation-bar">
              <div v-if="isVirtualEnabled" class="operation-wrap">
                <i class="icon-cog"></i>
                <span>{{ $t('Household.DeviceRFIDDetailDialog.Labels.HouseholdTypeSetting') }}</span>
                <el-tooltip :content="$t('Household.DeviceRFIDDetailDialog.Tooltips.ChangeHouseholdType')" placement="top">
                  <el-button type="success" size="small" @click="onStartTransferHousehold">
                    {{ $t('Household.DeviceRFIDDetailDialog.Buttons.StartTransfer') }}
                  </el-button>
                </el-tooltip>
              </div>
              <div class="operation-wrap">
                <i class="icon-signal"></i>
                <span>{{ $t('Household.DeviceRFIDDetailDialog.Labels.ScanDevice') }}</span>
                <div class="button-group">
                  <el-tooltip :content="$t('Household.DeviceRFIDDetailDialog.Tooltips.ConfirmDevice')" placement="top">
                    <el-button type="primary" size="small" @click="findBarcode">
                      {{ $t('Household.DeviceRFIDDetailDialog.Buttons.ConfirmDevice') }}
                    </el-button>
                  </el-tooltip>
                  <el-tooltip :content="$t('Household.DeviceRFIDDetailDialog.Tooltips.QuickRemoveDevice')" placement="top">
                    <el-button type="danger" size="small" @click="onQuickRemoveDevice">
                      {{ $t('Household.DeviceRFIDDetailDialog.Buttons.QuickRemoveDevice') }}
                    </el-button>
                  </el-tooltip>
                </div>
              </div>
            </div>
            <!-- 裝置列表表格 -->
            <el-table
              ref="deviceTable"
              :data="deviceList"
              :empty-text="$t('Household.DeviceRFIDDetailDialog.EmptyText.Device')"
              class="device-table"
              :row-class-name="getDeviceRowClassName"
            >
              <el-table-column
                prop="Name"
                :label="$t('Household.DeviceRFIDDetailDialog.TableHeaders.Resident')"
                align="center"
                header-align="center"
                :width="isVirtualEnabled ? '100px' : '160px'"
              />
              <el-table-column
                v-if="isVirtualEnabled"
                prop="category"
                :label="$t('Household.DeviceRFIDDetailDialog.TableHeaders.Household')"
                align="center"
                header-align="center"
                width="180"
              >
                <template slot-scope="scope">
                  <span v-if="!isHouseholdSelectVisible || activeTab !== 'device'">
                    {{ scope.row.IsVirtual === 1 ? $t('Household.DeviceRFIDDetailDialog.HouseholdType.Tenant', { virtualName: scope.row.VirtualName }) : $t('Household.DeviceRFIDDetailDialog.HouseholdType.Owner') }}
                  </span>
                  <el-select
                    v-else
                    v-model="scope.row.selectedTabletId"
                    :placeholder="$t('Household.DeviceRFIDDetailDialog.Placeholders.SelectHousehold')"
                    size="small"
                    :class="{ 'is-error': hasDeviceDuplicate(scope.row) }"
                  >
                    <el-option
                      v-for="tablet in householdOptions"
                      :key="tablet.TabletId"
                      :label="tablet.IsVirtual === 1 ? $t('Household.DeviceRFIDDetailDialog.HouseholdType.Tenant', { virtualName: tablet.VirtualName }) : $t('Household.DeviceRFIDDetailDialog.HouseholdType.Owner')"
                      :value="tablet.TabletId"
                    />
                  </el-select>
                </template>
              </el-table-column>
              <el-table-column
                :label="$t('Household.DeviceRFIDDetailDialog.TableHeaders.MailPrivacy')"
                align="center"
                header-align="center"
                width="100px"
              >
                <template slot-scope="scope">
                  {{ scope.row.IsPrivacy ? $t('Household.DeviceRFIDDetailDialog.Status.Privacy') : '' }}
                </template>
              </el-table-column>
              <el-table-column
                prop="Device"
                :label="$t('Household.DeviceRFIDDetailDialog.TableHeaders.DeviceSystem')"
                align="center"
                header-align="center"
                width="100px"
              />
              <el-table-column
                prop="Model"
                :label="$t('Household.DeviceRFIDDetailDialog.TableHeaders.DeviceModel')"
                align="center"
                header-align="center"
                :width="isVirtualEnabled ? '100px' : '160px'"
              >
                <template slot-scope="scope">
                  {{ scope.row.Model ? getDeviceModel(scope.row.Model) : '-' }}
                </template>
              </el-table-column>
              <el-table-column
                prop="Version"
                :label="$t('Household.DeviceRFIDDetailDialog.TableHeaders.DeviceVersion')"
                align="center"
                header-align="center"
                width="100px"
              />
              <el-table-column
                prop="InstallAt"
                :label="$t('Household.DeviceRFIDDetailDialog.TableHeaders.InstallDate')"
                align="center"
                header-align="center"
                :width="isVirtualEnabled ? '100px' : '160px'"
              >
                <template slot-scope="scope">
                  <div v-if="scope.row.InstallAt">
                    <div v-if="isVirtualEnabled">
                      <div>{{ formatDate(scope.row.InstallAt) }}</div>
                      <div>{{ formatTime(scope.row.InstallAt) }}</div>
                    </div>
                    <div v-else>
                      <div>{{ formatDate(scope.row.InstallAt) }} {{ formatTime(scope.row.InstallAt) }}</div>
                    </div>
                  </div>
                </template>
              </el-table-column>
              <el-table-column
                :label="$t('Household.DeviceRFIDDetailDialog.TableHeaders.Action')"
                align="center"
                header-align="center"
                width="125px"
              >
                <template slot-scope="scope">
                  <el-button
                    type="danger"
                    size="mini"
                    @click="onRemoveDevice(scope.row)"
                  >
                    {{ $t('Household.DeviceRFIDDetailDialog.Buttons.RemoveDevice') }}
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
          <!-- NFC載具 Tab -->
          <el-tab-pane name="carrier">
            <el-tooltip
              slot="label"
              :content="rfidTabTooltip"
              placement="top"
              :disabled="!isVirtualEnabled"
            >
              <span>
                <el-badge :value="rfidList.length" class="read-dialog__badge"> {{ $t('Household.DeviceRFIDDetailDialog.Tabs.Carrier') }} </el-badge>
              </span>
            </el-tooltip>

            <!-- 操作按鈕區域 -->
            <div class="operation-bar">
              <div v-if="isVirtualEnabled" class="operation-wrap">
                <i class="icon-cog"></i>
                <span>{{ $t('Household.DeviceRFIDDetailDialog.Labels.HouseholdTypeSetting') }}</span>
                <el-tooltip :content="$t('Household.DeviceRFIDDetailDialog.Tooltips.ChangeCarrierType')" placement="top">
                  <el-button type="success" size="small" @click="onStartTransferHousehold">
                    {{ $t('Household.DeviceRFIDDetailDialog.Buttons.StartTransfer') }}
                  </el-button>
                </el-tooltip>
              </div>
              <div class="operation-wrap">
                <i class="icon-signal"></i>
                <span>{{ $t('Household.DeviceRFIDDetailDialog.Labels.ScanCard') }}</span>
                <div class="button-group">
                  <el-tooltip :content="$t('Household.DeviceRFIDDetailDialog.Tooltips.ConfirmCarrier')" placement="top">
                    <el-button type="primary" size="small" @click="findCarrier">
                      {{ $t('Household.DeviceRFIDDetailDialog.Buttons.ConfirmCarrier') }}
                    </el-button>
                  </el-tooltip>
                  <el-tooltip :content="$t('Household.DeviceRFIDDetailDialog.Tooltips.QuickRemoveCarrier')" placement="top">
                    <el-button type="danger" size="small" @click="onQuickRemoveCarrier">
                      {{ $t('Household.DeviceRFIDDetailDialog.Buttons.QuickRemoveCarrier') }}
                    </el-button>
                  </el-tooltip>
                </div>
              </div>
            </div>
            <!-- NFC載具列表表格 -->
            <el-table
              ref="rfidTable"
              :data="rfidList"
              :empty-text="$t('Household.DeviceRFIDDetailDialog.EmptyText.Carrier')"
              class="nfc-table"
              :row-class-name="getCarrierRowClassName"
            >
              <el-table-column
                v-if="isVirtualEnabled"
                :label="$t('Household.DeviceRFIDDetailDialog.TableHeaders.Household')"
                align="center"
                header-align="center"
                width="155"
              >
                <template slot-scope="scope">
                  <span v-if="!isHouseholdSelectVisible || activeTab !== 'carrier'">
                    {{ scope.row.IsVirtual === 1 ? $t('Household.DeviceRFIDDetailDialog.HouseholdType.Tenant', { virtualName: scope.row.VirtualName }) : $t('Household.DeviceRFIDDetailDialog.HouseholdType.Owner') }}
                  </span>
                  <el-select
                    v-else
                    v-model="scope.row.selectedTabletId"
                    :placeholder="$t('Household.DeviceRFIDDetailDialog.Placeholders.SelectHousehold')"
                    size="small"
                    :class="{ 'is-error': hasRfidDuplicate(scope.row) }"
                  >
                    <el-option
                      v-for="tablet in householdOptions"
                      :key="tablet.TabletId"
                      :label="tablet.IsVirtual === 1 ? $t('Household.DeviceRFIDDetailDialog.HouseholdType.Tenant', { virtualName: tablet.VirtualName }) : $t('Household.DeviceRFIDDetailDialog.HouseholdType.Owner')"
                      :value="tablet.TabletId"
                    />
                  </el-select>
                </template>
              </el-table-column>
              <el-table-column
                prop="CarrierCardId"
                :label="$t('Household.DeviceRFIDDetailDialog.TableHeaders.SerialNumber')"
                align="center"
                header-align="center"
                :width="isVirtualEnabled ? '525px' : '620px'"
              />
              <el-table-column
                prop="CarrierAt"
                :label="$t('Household.DeviceRFIDDetailDialog.TableHeaders.RegisterDate')"
                align="center"
                header-align="center"
                :width="isVirtualEnabled ? '100px' : '160px'"
              >
                <template slot-scope="scope">
                  <div v-if="scope.row.CarrierAt">
                    <div v-if="isVirtualEnabled">
                      <div>{{ formatDate(scope.row.CarrierAt) }}</div>
                      <div>{{ formatTime(scope.row.CarrierAt) }}</div>
                    </div>
                    <div v-else>
                      <div>{{ formatDate(scope.row.CarrierAt) }} {{ formatTime(scope.row.CarrierAt) }}</div>
                    </div>
                  </div>
                </template>
              </el-table-column>
              <el-table-column :label="$t('Household.DeviceRFIDDetailDialog.TableHeaders.Action')" align="center" header-align="center" width="125">
                <template slot-scope="scope">
                  <el-button
                    type="danger"
                    size="mini"
                    @click="onRemoveRFID(scope.row)"
                  >
                    {{ $t('Household.DeviceRFIDDetailDialog.Buttons.RemoveCarrier') }}
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
        </el-tabs>
      </div>
      <!-- Dialog 底部按鈕 -->
      <div v-if="isHouseholdSelectVisible" slot="footer" class="dialog-footer">
        <el-button type="primary" @click="onSaveChanges">
          {{ $t('Household.DeviceRFIDDetailDialog.Buttons.SaveChanges') }}
        </el-button>
        <el-button @click="onCancelEdit">
          {{ $t('General.Cancel') }}
        </el-button>
      </div>
    </el-dialog>
    <reader ref="reader" />
    <el-dialog
      class="quick-unbind-dialog"
      :title="quickUnbindDialogTitle"
      :visible.sync="isQuickUnbindDialogVisible"
      width="420px"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      @close="onCancelQuickUnbind"
    >
      <div class="quick-unbind-dialog__content">
        <el-select
          v-model="selectedQuickUnbindTabletId"
          :placeholder="$t('Household.DeviceRFIDDetailDialog.Placeholders.SelectHousehold')"
          filterable
        >
          <el-option
            v-for="tablet in quickUnbindOptions"
            :key="tablet.TabletId"
            :label="tablet.TabletNote"
            :value="tablet.TabletId"
          />
        </el-select>
      </div>
      <span slot="footer" class="quick-unbind-dialog__footer">
        <el-button @click="onCancelQuickUnbind">
          {{ $t('General.Cancel') }}
        </el-button>
        <el-button
          type="primary"
          :disabled="!selectedQuickUnbindTabletId || isQuickUnbindSubmitting"
          @click="onConfirmQuickUnbind"
        >
          {{ $t('General.Confirm') }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { housekeeperAPI } from '@/api/HousekeeperAPI.js'
import { getDeviceModel } from '@/js/models/appleDevice.js'
import ReadNFCBarcode from '@/components/ReadNFCBarcode'
export default {
  name: 'DeviceRFIDDetailDialog',
  components: {
    reader: ReadNFCBarcode
  },
  data () {
    return {
      dialogVisible: false,
      activeTab: 'device', // 預設顯示手機裝置
      tabletId: '',
      tabletNote: '',
      deviceList: [],
      rfidList: [],
      highlightedDeviceSn: '', // 聚焦顯示的設備序號
      highlightDeviceTimeout: null, // 裝置聚焦計時器
      highlightedCarrierIdList: [], // 聚焦顯示的載具 ID 列表
      highlightedCarrierTimeout: null, // 載具聚焦計時器
      isHouseholdSelectVisible: false, // 是否顯示戶別選擇 dropdown
      householdOptions: [], // 戶別選項列表
      hasClickedSave: false, // 是否已點擊儲存修改
      isQuickUnbindDialogVisible: false, // 是否顯示快速解除對話框
      quickUnbindOptions: [], // 快速解除可選戶別列表
      selectedQuickUnbindTabletId: '', // 快速解除選取的戶別 ID
      quickUnbindTargetCode: '', // 快速解除當前掃描的裝置或載具序號
      isQuickUnbindSubmitting: false // 快速解除是否送出中
    }
  },
  computed: {
    isVirtualEnabled () {
      return this.$store.getters.isServiceEnabled(14) // 社區開啟承租戶功能
    },
    isEditing () {
      return this.isHouseholdSelectVisible
    },
    /**
     * 計算手機裝置中區權戶與承租戶的數量
     */
    deviceTabTooltip () {
      if (!this.isVirtualEnabled) return ''
      const regionalCount = this.deviceList.filter(device => device.IsVirtual === 0).length
      const virtualCount = this.deviceList.filter(device => device.IsVirtual === 1).length
      return this.$t('Household.DeviceRFIDDetailDialog.Tooltips.CountInfo', { regionalCount, virtualCount })
    },
    /**
     * 計算載具中區權戶與承租戶的數量
     */
    rfidTabTooltip () {
      if (!this.isVirtualEnabled) return ''
      const regionalCount = this.rfidList.filter(rfid => rfid.IsVirtual === 0).length
      const virtualCount = this.rfidList.filter(rfid => rfid.IsVirtual === 1).length
      return this.$t('Household.DeviceRFIDDetailDialog.Tooltips.CountInfo', { regionalCount, virtualCount })
    },
    /**
     * 取得快速解除對話框標題
     */
    quickUnbindDialogTitle () {
      if (this.activeTab === 'device') {
        return this.$t('Household.DeviceRFIDDetailDialog.Titles.QuickUnbindDevice')
      }
      if (this.activeTab === 'carrier') {
        return this.$t('Household.DeviceRFIDDetailDialog.Titles.QuickUnbindCarrier')
      }
      return ''
    }
  },
  methods: {
    async open (tabletId, tabletNote) {
      this.tabletId = tabletId
      this.tabletNote = tabletNote

      const deviceRes = await housekeeperAPI.getCommunityTabletDeviceList(tabletId)
      this.deviceList = deviceRes.data.Data.CommunityTablets.map(el => ({
        ...el,
        selectedTabletId: el.TabletId
      }))

      const rfidRes = await housekeeperAPI.getCommunityTabletCarrierList(tabletId)
      this.rfidList = rfidRes.data.Data.CommunityTablets.map(el => ({
        ...el,
        selectedTabletId: el.TabletId
      }))

      this.dialogVisible = true
    },
    /**
     * 如果還在轉戶中，阻止 dialog 關閉
     */
    beforeClose (done) {
      if (this.isHouseholdSelectVisible) {
        this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.PleaseCompleteTransfer'), this.$t('General.AlertTitle'), {
          confirmButtonText: this.$t('General.OK'),
          showClose: false,
          type: 'warning'
        })
      } else {
        done()
      }
    },
    /**
     * 關閉 dialog 事件
     */
    onCloseDialog () {
      // 預設顯示手機裝置
      this.activeTab = 'device'

      // 清除列表資料
      this.deviceList = []
      this.rfidList = []

      // 清除聚焦狀態和計時器
      this.highlightedDeviceSn = ''
      if (this.highlightDeviceTimeout) {
        clearTimeout(this.highlightDeviceTimeout)
        this.highlightDeviceTimeout = null
      }

      // 重置戶別轉換相關狀態
      this.isHouseholdSelectVisible = false
      this.householdOptions = []
      this.hasClickedSave = false

      // 重置快速解除裝置、載具狀態
      this.resetQuickUnbindDialogState()
    },
    findBarcode () {
      // 裝置確認
      this.$refs.reader.getCode((code) => {
        const upperCode = code.toUpperCase()
        this.$refs.reader.close()
        this.cancelHighlightDevice()
        // 尋找符合的設備
        const foundIndex = this.deviceList.findIndex(device => device.DeviceSn === upperCode)
        if (foundIndex !== -1) {
          // 設定聚焦的設備序號
          this.highlightDevice(upperCode)
          this.tableScrollToRow(this.$refs.deviceTable, foundIndex)
        } else {
          this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.DeviceNotFound'), this.$t('General.AlertTitle'), {
            confirmButtonText: this.$t('General.OK'),
            showClose: false,
            type: 'warning'
          })
        }
        this.highlightDevice(upperCode)
      }, 'Barcode')
    },
    findCarrier () {
      // 載具確認
      this.$refs.reader.getCode(async (code) => {
        try {
          this.$refs.reader.close()
          const { data } = await housekeeperAPI.getCommunityTabletCarrierByTabletAndCode(this.tabletId, code)
          const carrierIdListByCode = data.Data.CommunityTablets.map(el => el.CarrierId)
          const carrierIdListByTablet = this.rfidList.map(el => el.CarrierId)
          const foundIndex = carrierIdListByTablet.findIndex(id => carrierIdListByCode.includes(id))
          this.cancelHighlightCarriers()
          if (foundIndex === -1) {
            throw new Error('查無載具')
          } else {
            this.highlightCarriers(carrierIdListByCode)
            this.tableScrollToRow(this.$refs.rfidTable, foundIndex)
          }
        } catch {
          this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.CarrierNotFound'), this.$t('General.AlertTitle'), {
            confirmButtonText: this.$t('General.OK'),
            showClose: false,
            type: 'warning'
          })
        }
      }, {
        type: 'NFC',
        model: 'basic',
        title: this.$t('FastReceive.PlaceCarrierOnSensor'),
        subtitle: this.$t('FastReceive.AccessCardNote')
      })
    },
    tableScrollToRow (tableRef, index) {
      this.$nextTick(() => {
        const tableBody = tableRef.bodyWrapper
        const row = tableBody.querySelectorAll('.el-table__row')[index]
        if (row) {
          row.scrollIntoView({ block: 'center' })
        }
      })
    },
    cancelHighlightDevice () {
      this.highlightedDeviceSn = ''
      clearTimeout(this.highlightDeviceTimeout)
    },
    cancelHighlightCarriers () {
      this.highlightedCarrierIdList = []
      clearTimeout(this.highlightedCarrierTimeout)
    },
    /**
     * 根據掃描的 code 聚焦對應的設備
     * @param {String} code - 掃描到的設備序號
     */
    highlightDevice (code) {
      this.highlightedDeviceSn = code
      this.highlightDeviceTimeout = setTimeout(() => {
        this.highlightedDeviceSn = ''
        clearTimeout(this.highlightDeviceTimeout)
      }, 5000)
    },
    highlightCarriers (carrierIds) {
      this.highlightedCarrierIdList = carrierIds
      this.highlightedCarrierTimeout = setTimeout(() => {
        this.highlightedCarrierIdList = []
        clearTimeout(this.highlightedCarrierTimeout)
      }, 5000)
    },
    /**
     * 設定表格行的 class name
     * @param {Object} params - 包含 row 和 rowIndex 的參數
     * @returns {String} class name
     */
    getDeviceRowClassName ({ row }) {
      return row.DeviceSn === this.highlightedDeviceSn ? 'highlighted-row' : ''
    },
    getCarrierRowClassName ({ row }) {
      return this.highlightedCarrierIdList.includes(row.CarrierId) ? 'highlighted-row' : ''
    },

    /**
     * 解除裝置
     * @param {Object} device - 裝置資料
     */
    async onRemoveDevice (device) {
      try {
        await this.$confirm(this.$t('Household.DeviceRFIDDetailDialog.Messages.ConfirmRemoveDevice'), this.$t('General.AlertTitle'), {
          confirmButtonText: this.$t('General.OK'),
          cancelButtonText: this.$t('General.Cancel'),
          type: 'warning',
          showClose: false
        })
      } catch {
        // 點擊取消
        return
      }

      try {
        await housekeeperAPI.unbindCommunityTabletDevice(device.TabletId, device.DeviceSn)
        const targetIndex = this.deviceList.findIndex(item => item.DeviceSn === device.DeviceSn)
        if (targetIndex !== -1) {
          this.deviceList.splice(targetIndex, 1)
        }
        this.$emit('unbind-success', {
          tabletId: device.TabletId,
          type: 'device'
        })
        await this.$confirm(this.$t('Household.DeviceRFIDDetailDialog.Messages.DeviceRemoved'), this.$t('General.AlertTitle'), {
          confirmButtonText: this.$t('General.OK'),
          showClose: false,
          showCancelButton: false,
          type: 'success'
        })
      } catch (error) {
        console.error(error)
        await this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.DeviceRemoveFailed'), this.$t('General.AlertTitle'), {
          confirmButtonText: this.$t('General.OK'),
          showClose: false,
          type: 'warning'
        })
      }
    },

    /**
     * 解除 RFID 載具
     * @param {Object} rfid - RFID 載具資料
     */
    async onRemoveRFID (nfc) {
      try {
        await this.$confirm(this.$t('Household.DeviceRFIDDetailDialog.Messages.ConfirmRemoveCarrier'), this.$t('General.AlertTitle'), {
          confirmButtonText: this.$t('General.OK'),
          cancelButtonText: this.$t('General.Cancel'),
          type: 'warning',
          showClose: false
        })
      } catch (error) {
        console.error(error)
        return
      }

      try {
        await housekeeperAPI.unbindCommunityTabletCarrier(nfc.TabletId, nfc.CarrierCardId)
        const targetIndex = this.rfidList.findIndex(item => item.CarrierCardId === nfc.CarrierCardId)
        if (targetIndex !== -1) {
          this.rfidList.splice(targetIndex, 1)
        }
        this.$emit('unbind-success', {
          tabletId: nfc.TabletId,
          type: 'carrier'
        })
        await this.$confirm(this.$t('Household.DeviceRFIDDetailDialog.Messages.CarrierRemoved'), this.$t('General.AlertTitle'), {
          confirmButtonText: this.$t('General.OK'),
          showClose: false,
          showCancelButton: false,
          type: 'success'
        })
      } catch (error) {
        console.error(error)
        await this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.CarrierRemoveFailed'), this.$t('General.AlertTitle'), {
          confirmButtonText: this.$t('General.OK'),
          showClose: false,
          type: 'warning'
        })
      }
    },

    /**
     * 格式化日期部分（年/月/日）
     * @param {String} datetime - 完整的日期時間字串
     * @returns {String} 格式化後的日期
     */
    formatDate (datetime) {
      if (!datetime) return ''
      return dayjs(datetime).format('YYYY/MM/DD')
    },

    /**
     * 格式化時間部分（時:分）
     * @param {String} datetime - 完整的日期時間字串
     * @returns {String} 格式化後的時間
     */
    formatTime (datetime) {
      if (!datetime) return ''
      return dayjs(datetime).format('HH:mm')
    },

    /**
     * 開始轉戶功能
     */
    async onStartTransferHousehold () {
      if (this.activeTab === 'device' && this.deviceList.length === 0) {
        this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.NoDeviceToTransfer'), this.$t('General.AlertTitle'), {
          confirmButtonText: this.$t('General.OK'),
          showClose: false,
          type: 'warning'
        })
        return
      }

      if (this.activeTab === 'carrier' && this.rfidList.length === 0) {
        this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.NoCarrierToTransfer'), this.$t('General.AlertTitle'), {
          confirmButtonText: this.$t('General.OK'),
          showClose: false,
          type: 'warning'
        })
        return
      }

      try {
        const { data } = await housekeeperAPI.getTabletRegionalAndVirtual(this.tabletId)
        this.householdOptions = data.Data.CommunityTablets
        this.isHouseholdSelectVisible = true
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
        this.$message.error(this.$t('Household.DeviceRFIDDetailDialog.Messages.GetHouseholdOptionsFailed'))
      }
    },

    /**
     * Tab 切換前的檢查
     * @param {String} activeName - 要切換到的 tab 名稱
     * @param {String} oldActiveName - 目前的 tab 名稱
     * @returns {Boolean|Promise} 是否允許切換
     */
    onBeforeLeave (activeName, oldActiveName) {
      // 如果正在編輯狀態，詢問使用者是否要取消編輯
      if (this.isEditing) {
        this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.CompleteEditFirst'), this.$t('General.AlertTitle'), {
          confirmButtonText: this.$t('General.OK'),
          showClose: false,
          type: 'warning'
        }).then(() => {
        }).catch(() => {
        })
        return false
      }
      return true
    },

    /**
     * 取消編輯
     */
    onCancelEdit () {
      // 復原裝置列表的 selectedTabletId
      this.deviceList.forEach(device => {
        device.selectedTabletId = device.TabletId
      })

      // 復原 RFID 載具列表的 selectedTabletId
      this.rfidList.forEach(rfid => {
        rfid.selectedTabletId = rfid.TabletId
      })

      this.isHouseholdSelectVisible = false
    },

    /**
     * 儲存修改
     * 根據當前 tab 呼叫不同的 API
     */
    async onSaveChanges () {
      this.hasClickedSave = true
      try {
        if (this.activeTab === 'device') {
          // 如果是在裝置頁
          const hasChanges = this.deviceList.some(device => device.TabletId !== device.selectedTabletId)
          if (!hasChanges) {
            this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.TransferSuccess'), this.$t('General.AlertTitle'), {
              showClose: false,
              type: 'success'
            })
            this.isHouseholdSelectVisible = false
            return
          }

          // 檢查是否有重複的 DeviceSn + TabletId 組合
          if (this.checkDeviceDuplicates()) {
            this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.DeviceTransferFailedDuplicate'), this.$t('General.AlertTitle'), {
              showClose: false,
              type: 'warning'
            })
            return
          }

          const AdjustTablets = this.deviceList.map(el => ({
            UserId: el.UserId,
            TabletId: el.selectedTabletId
          }))
          const adjustData = { AdjustTablets }
          await housekeeperAPI.adjustCommunityTabletDevice(this.tabletId, adjustData)
          // 更新呈現
          this.deviceList.forEach(device => {
            device.TabletId = device.selectedTabletId
            const selected = this.householdOptions.find(
              tablet => tablet.TabletId === device.selectedTabletId
            )
            device.IsVirtual = selected.IsVirtual
            device.VirtualName = selected.VirtualName
          })
        } else if (this.activeTab === 'carrier') {
          // 如果是在 rfid 頁
          const hasChanges = this.rfidList.some(rfid => rfid.TabletId !== rfid.selectedTabletId)
          if (!hasChanges) {
            this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.TransferSuccess'), this.$t('General.AlertTitle'), {
              showClose: false,
              type: 'success'
            })
            this.isHouseholdSelectVisible = false
            return
          }

          // 檢查是否有重複的 CarrierCardId + TabletId 組合
          if (this.checkRfidDuplicates()) {
            this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.CarrierTransferFailedDuplicate'), this.$t('General.AlertTitle'), {
              showClose: false,
              type: 'warning'
            })
            return
          }

          const AdjustTablets = this.rfidList.map(el => ({
            CarrierId: el.CarrierId,
            TabletId: el.selectedTabletId
          }))
          const adjustData = { AdjustTablets }
          await housekeeperAPI.adjustCommunityTabletCarrier(this.tabletId, adjustData)
          // 更新呈現
          this.rfidList.forEach(rfid => {
            rfid.TabletId = rfid.selectedTabletId
            const selected = this.householdOptions.find(
              tablet => tablet.TabletId === rfid.selectedTabletId
            )
            rfid.IsVirtual = selected.IsVirtual
            rfid.VirtualName = selected.VirtualName
          })
        }

        this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.TransferSuccess'), this.$t('General.AlertTitle'), {
          showClose: false,
          type: 'success'
        })
        this.isHouseholdSelectVisible = false
      } catch (error) {
        this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.TransferFailed'), this.$t('General.AlertTitle'), {
          showClose: false,
          type: 'warning'
        })
        console.error(error)
      }
    },

    /**
     * 檢查裝置是否有重複的 DeviceSn + TabletId 組合
     * @param {Object} row - 當前行的資料
     * @returns {Boolean} 是否重複
     */
    hasDeviceDuplicate (row) {
      // 只有在點擊儲存後才進行重複檢查
      if (!this.hasClickedSave) return false

      const currentKey = `${row.DeviceSn}_${row.selectedTabletId}`
      const duplicateCount = this.deviceList.filter(item =>
        `${item.DeviceSn}_${item.selectedTabletId}` === currentKey
      ).length

      return duplicateCount > 1
    },

    /**
     * 檢查 RFID 載具是否有重複的 CarrierCardId + TabletId 組合
     * @param {Object} row - 當前行的資料
     * @returns {Boolean} 是否重複
     */
    hasRfidDuplicate (row) {
      // 只有在點擊儲存後才進行重複檢查
      if (!this.hasClickedSave) return false

      const currentKey = `${row.CarrierCardId}_${row.selectedTabletId}`
      const duplicateCount = this.rfidList.filter(item =>
        `${item.CarrierCardId}_${item.selectedTabletId}` === currentKey
      ).length

      return duplicateCount > 1
    },

    /**
     * 檢查整體裝置列表是否存在重複項目
     * @returns {Boolean} 是否存在重複項目
     */
    checkDeviceDuplicates () {
      const keySet = new Set()

      for (const item of this.deviceList) {
        const key = `${item.DeviceSn}_${item.selectedTabletId}`
        if (keySet.has(key)) {
          return true // 發現重複
        }
        keySet.add(key)
      }

      return false // 沒有重複
    },

    /**
     * 檢查整體 RFID 列表是否存在重複項目
     * @returns {Boolean} 是否存在重複項目
     */
    checkRfidDuplicates () {
      const keySet = new Set()

      for (const item of this.rfidList) {
        const key = `${item.CarrierCardId}_${item.selectedTabletId}`
        if (keySet.has(key)) {
          return true // 發現重複
        }
        keySet.add(key)
      }

      return false // 沒有重複
    },

    /**
     * 快速解除裝置流程
     */
    onQuickRemoveDevice () {
      this.$refs.reader.getCode(async (code) => {
        const upperCode = code.toUpperCase()
        this.$refs.reader.close()
        const matchedDevices = this.deviceList.filter(device => device.DeviceSn === upperCode)
        if (!matchedDevices.length) {
          await this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.DeviceNotFound'), this.$t('General.AlertTitle'), {
            confirmButtonText: this.$t('General.Confirm'),
            showClose: false,
            type: 'warning'
          })
          return
        }

        this.quickUnbindTargetCode = code

        if (matchedDevices.length === 1) {
          await this.unbindDeviceByCode({
            tabletId: matchedDevices[0].TabletId,
            deviceSn: code
          })
          return
        }

        const uniqueTabletMap = {}
        matchedDevices.forEach(device => {
          if (!uniqueTabletMap[device.TabletId]) {
            uniqueTabletMap[device.TabletId] = {
              TabletId: device.TabletId,
              TabletNote: device.TabletNote
            }
          }
        })

        this.quickUnbindOptions = Object.values(uniqueTabletMap)
        this.selectedQuickUnbindTabletId = ''
        this.isQuickUnbindDialogVisible = true
      }, 'Barcode')
    },

    /**
     * 快速解除載具流程
     */
    async onQuickRemoveCarrier () {
      this.$refs.reader.getCode(async (code) => {
        this.$refs.reader.close()
        try {
          const { data } = await housekeeperAPI.getCommunityTabletCarrierByTabletAndCode(this.tabletId, code)
          const tablets = data?.Data?.CommunityTablets || []
          if (!tablets.length) {
            throw new Error('查無載具')
          }

          this.quickUnbindTargetCode = code
          if (tablets.length === 1) {
            await this.unbindCarrier({
              tabletId: tablets[0].TabletId,
              carrierCardId: tablets[0].CarrierCardId
            })
            return
          }

          this.quickUnbindOptions = tablets
          this.selectedQuickUnbindTabletId = ''
          this.isQuickUnbindDialogVisible = true
        } catch {
          this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.CarrierNotFound'), this.$t('General.AlertTitle'), {
            confirmButtonText: this.$t('General.Confirm'),
            showClose: false,
            type: 'warning'
          })
        }
      }, {
        type: 'NFC',
        model: 'basic',
        title: this.$t('FastReceive.PlaceCarrierOnSensor'),
        subtitle: this.$t('FastReceive.AccessCardNote')
      })
    },
    /**
     * 確認快速解除
     */
    onConfirmQuickUnbind () {
      if (this.activeTab === 'device') {
        this.unbindDeviceByCode({
          tabletId: this.selectedQuickUnbindTabletId,
          deviceSn: this.quickUnbindTargetCode
        })
      } else if (this.activeTab === 'carrier') {
        const target = this.quickUnbindOptions.find(t => t.TabletId === this.selectedQuickUnbindTabletId)
        if (target) {
          this.unbindCarrier({
            tabletId: this.selectedQuickUnbindTabletId,
            carrierCardId: target.CarrierCardId
          })
        }
      }
    },
    /**
     * 取消快速解除對話框
     */
    onCancelQuickUnbind () {
      this.resetQuickUnbindDialogState()
    },
    /**
     * 重設快速解除狀態
     */
    resetQuickUnbindDialogState () {
      this.isQuickUnbindDialogVisible = false
      this.quickUnbindOptions = []
      this.selectedQuickUnbindTabletId = ''
      this.quickUnbindTargetCode = ''
      this.isQuickUnbindSubmitting = false
    },
    /**
     * 依裝置序號解除綁定
     * @param {Object} params - 解除綁定參數
     * @param {String} params.tabletId - 選擇要解除的戶別 ID
     * @param {String} params.deviceSn - 裝置序號
     */
    async unbindDeviceByCode ({ tabletId, deviceSn }) {
      if (!deviceSn) {
        return
      }
      this.isQuickUnbindSubmitting = true
      try {
        await housekeeperAPI.unbindCommunityTabletDevice(tabletId, deviceSn)
        this.resetQuickUnbindDialogState()
        this.$emit('unbind-success', {
          tabletId,
          type: 'device'
        })
        await this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.DeviceRemoved'), this.$t('General.AlertTitle'), {
          confirmButtonText: this.$t('General.Confirm'),
          showClose: false,
          type: 'success'
        })
        // 更新呈現
        this.deviceList = this.deviceList.filter(device => !(device.TabletId === tabletId && device.DeviceSn === deviceSn))
      } catch (error) {
        console.error(error)
        await this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.DeviceRemoveFailed'), this.$t('General.AlertTitle'), {
          confirmButtonText: this.$t('General.Confirm'),
          showClose: false,
          type: 'warning'
        })
      } finally {
        this.isQuickUnbindSubmitting = false
      }
    },
    /**
     * 依載具碼解除綁定
     * @param {Object} params - 解除綁定參數
     * @param {String} params.tabletId - 選擇要解除的戶別 ID
     * @param {String} params.carrierCardId - 載具卡片識別碼
     */
    async unbindCarrier ({ tabletId, carrierCardId }) {
      if (!carrierCardId) {
        return
      }
      this.isQuickUnbindSubmitting = true
      try {
        await housekeeperAPI.unbindCommunityTabletCarrier(tabletId, carrierCardId)
        this.resetQuickUnbindDialogState()
        this.$emit('unbind-success', {
          tabletId,
          type: 'carrier'
        })
        await this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.CarrierRemoved'), this.$t('General.AlertTitle'), {
          confirmButtonText: this.$t('General.Confirm'),
          showClose: false,
          type: 'success'
        })
        // 更新呈現
        this.rfidList = this.rfidList.filter(rfid => !(tabletId === rfid.TabletId && rfid.CarrierCardId === carrierCardId))
      } catch (error) {
        console.error(error)
        await this.$alert(this.$t('Household.DeviceRFIDDetailDialog.Messages.CarrierRemoveFailed'), this.$t('General.AlertTitle'), {
          confirmButtonText: this.$t('General.Confirm'),
          showClose: false,
          type: 'warning'
        })
      } finally {
        this.isQuickUnbindSubmitting = false
      }
    },
    getDeviceModel (code) {
      return getDeviceModel(code)
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .device-rfid-dialog {
  .el-dialog {
    @include basicDialog();
    margin: 0 auto auto;
    .el-dialog__body {
      height: 60vh;
      overflow-y: hidden;
      .device-dialog-content {
        height: 100%;
        overflow: hidden;
        .el-tabs {
          .el-badge__content {
            top: 13px;
            right: -3px;
          }
          height: 100%;
          display: flex;
          flex-direction: column;
          .el-tabs__content {
            overflow-y: auto;
          }
          .operation-bar {
            padding: 8px;
            display: flex;
            align-items: center;
            justify-content: end;
            flex-wrap: wrap;
            gap: 8px;
            .el-button {
              margin: 0;
            }
            .operation-wrap {
              display: flex;
              align-items: center;
              i {
                padding-right: 8px;
              }
              .button-group {
                display: flex;
                align-items: center;
                gap: 8px;
              }
            }
          }
          .el-table {
            @include basicTable();

            // 聚焦行樣式
            .highlighted-row {
              background-color: $lightYellowColor;
            }

            .is-error .el-input__inner {
              border: 1px solid $dangerColor;
            }
          }
        }
      }
    }

    .dialog-footer {
      text-align: center;
    }
  }
}

::v-deep .quick-unbind-dialog {
  .el-dialog{
    @include basicDialog();
    .el-dialog__body {
      overflow-y: hidden;
    }

    .quick-unbind-dialog__content {
      .el-select {
        width: 100%;
      }
    }

    .quick-unbind-dialog__footer {
      display: flex;
      justify-content: center;
    }
  }
}
</style>
